import { useState } from "react";
import { User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { COUNTRY_CODES, DECISION_MAKERS_OPTIONS, type LeadFormData } from "../types";

interface Props {
  data: LeadFormData;
  onChange: (patch: Partial<LeadFormData>) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function StepDetails({ data, onChange }: Props) {
  const [emailTouched, setEmailTouched] = useState(false);
  const emailValue = data.email?.trim() ?? "";
  const emailInvalid = emailTouched && emailValue.length > 0 && !EMAIL_RE.test(emailValue);

  const handleFocus = (e: React.FocusEvent<HTMLElement>) => {
    requestAnimationFrame(() => {
      e.target.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  return (
    <div className="space-y-6" onFocus={handleFocus}>
      <div className="space-y-3">
        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-blue">
          <User className="h-4 w-4" />
          Your Details
        </div>
        <h2 className="text-balance text-3xl font-bold tracking-tighter text-foreground sm:text-4xl">
          Almost there - where should we send it?
        </h2>
        <p className="text-base text-muted-foreground">
          Next, pick a quick time so we can walk you through your mockup live. No spam, ever.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-10">
        <div className="space-y-4 md:col-span-3">
          <div className="space-y-2">
            <label htmlFor="fullName" className="text-sm font-semibold text-foreground">
              Your Name <span className="text-brand-blue">*</span>
            </label>
            <Input
              id="fullName"
              type="text"
              placeholder="John Smith"
              maxLength={100}
              value={data.fullName ?? ""}
              onChange={(e) => onChange({ fullName: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-semibold text-foreground">
              Email Address <span className="text-brand-blue">*</span>
            </label>
            <Input
              id="email"
              type="email"
              placeholder="you@business.com"
              maxLength={255}
              value={data.email ?? ""}
              onChange={(e) => onChange({ email: e.target.value })}
              onBlur={() => setEmailTouched(true)}
              className={cn(emailInvalid && "border-destructive focus-visible:ring-destructive")}
            />
            {emailInvalid && (
              <p className="text-xs text-destructive">Please enter a valid email address.</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="phone" className="text-sm font-semibold text-foreground">
              Phone Number <span className="text-brand-blue">*</span>
            </label>
            <div className="flex gap-2">
              <select
                aria-label="Country code"
                value={data.phoneCountry ?? "us"}
                onChange={(e) => onChange({ phoneCountry: e.target.value })}
                className="h-9 rounded-md border border-input bg-transparent px-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label} {c.dial}
                  </option>
                ))}
              </select>
              <Input
                id="phone"
                type="tel"
                placeholder="7XXX XXXXXX"
                maxLength={20}
                value={data.phoneNumber ?? ""}
                onChange={(e) => onChange({ phoneNumber: e.target.value })}
                className="flex-1"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="instagramHandle" className="text-sm font-semibold text-foreground">
              Instagram Handle <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <Input
              id="instagramHandle"
              type="text"
              placeholder="@yourbusiness"
              maxLength={100}
              value={data.instagramHandle ?? ""}
              onChange={(e) => onChange({ instagramHandle: e.target.value })}
            />
          </div>
        </div>

        <div className="space-y-4 md:col-span-7">


          <div className="space-y-2">
            <label className="text-sm font-semibold text-foreground">
              Who else will be involved in this decision? <span className="text-brand-blue">*</span>
            </label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {DECISION_MAKERS_OPTIONS.map((opt) => {
                const selected = data.decisionMakers === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange({ decisionMakers: opt.value })}
                    className={cn(
                      "rounded-xl border bg-background px-4 py-3 text-left text-sm font-medium text-gray-950 transition-all",
                      "hover:border-brand-blue hover:shadow-sm",
                      selected
                        ? "border-brand-blue bg-brand-blue/5 ring-2 ring-brand-blue/30"
                        : "border-border",
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="websiteDescription" className="text-sm font-semibold text-foreground">
              Describe your ideal website <span className="text-brand-blue">*</span>
            </label>
            <p className="text-xs text-muted-foreground">
              What should your site say, feel like, or include? The more detail, the better your free mockup.
            </p>
            <Textarea
              id="websiteDescription"
              placeholder="E.g. I want a clean, modern site that builds trust. It should highlight my services, show testimonials, and make it easy to book a consultation..."
              maxLength={2000}
              rows={4}
              className="min-h-0 md:h-[99px]"
              value={data.websiteDescription ?? ""}
              onChange={(e) => onChange({ websiteDescription: e.target.value })}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
